<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col class="d-flex">
          <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0" />
        </v-col>
        <v-col class="text-right">
          <v-tooltip max-width="300" bottom>
            <template #activator="{ on }">
              <base-chip v-show="hasChanges" color="blue" text="Changes Detected" icon="info" dark small v-on="on">
              </base-chip>
            </template>
            <span>Click the FAB in the bottom right then click "Save Module" to keep your changes!</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-form ref="form">
      <v-container>
        <v-row>
          <v-col sm="4" class="order-sm-2">
            <v-row>
              <v-col>
                <v-card outlined>
                  <v-card-subtitle class="mb-6 grey lighten-4 pa-3">Details</v-card-subtitle>
                  <v-card-text>
                    <text-input v-model="formData.name" disabled label="Module Name" />
                    <text-input
                      v-model="formData.title"
                      :disabled="isLoading"
                      :readonly="!isPermittedTo('update')"
                      label="Title"
                      hide-details="auto"
                      required
                    />
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-card outlined>
                  <v-card-subtitle class="grey lighten-4 pa-3">Page Types</v-card-subtitle>
                  <v-list max-height="350" class="py-0 overflow-auto">
                    <template v-for="(pageType, index) in pageTypesUsingModule">
                      <v-list-item
                        :key="pageType.id"
                        :class="{ 'grey lighten-4': index % 2 !== 0 }"
                        :to="{ name: 'page-types.manage', params: { id: pageType.id } }"
                        dense
                      >
                        <div class="body-2">{{ pageType.name }}</div>
                      </v-list-item>
                    </template>
                  </v-list>
                  <v-card-actions class="d-block text-center px-0 pt-0 pb-3">
                    <v-divider class="pb-2" />
                    <span class="text-caption">Total Page Types: {{ pageTypesUsingModule.length }}</span>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
          <v-col sm="8" class="order-sm-1">
            <v-row>
              <v-col>
                <field-block-group
                  ref="field-block-group"
                  v-model="formData.fields"
                  :duplicate-keys="duplicateKeys"
                  :is-loading="isLoading"
                  :is-top-level="true"
                  :module-name="formData.name"
                  :readonly="!isPermittedTo('update')"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-form>

    <fab-actions
      :can-delete="isPermittedTo('delete')"
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      context="Module"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
      @delete="showDeleteModal = true"
    />

    <reset-modal
      v-model="showResetModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="module"
      @modal-confirm="onResetConfirm"
    />

    <!-- Save Modal -->
    <generic-modal
      v-model="showSaveModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Save Module"
      hide-louis
      persistent
      :is-confirm-disabled="!!duplicateKeys.length"
      @modal-confirm="onSaveConfirm"
      @modal-cancel="showSaveModal = false"
    >
      <div v-if="duplicateKeys.length">
        Module cannot be saved if there are <span class="error--text">duplicate</span> keys in Fields. Try updating
        titles to dynamically update the duplicate key. The following keys are duplicated:
        <ul class="mt-4 font-weight-bold">
          <li v-for="key in duplicateKeys" :key="key">{{ key }}</li>
        </ul>
      </div>
      <span v-else>Are you sure you want to save these changes?</span>
    </generic-modal>

    <!-- Delete Modal -->
    <generic-modal
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Delete Module"
      confirm-color="error"
      confirm-text="Delete"
      @modal-confirm="onDeleteConfirm"
    >
      <div>
        Are you sure you want to <span class="red--text font-weight-bold">DELETE</span> this module? This cannot be
        undone.
      </div>
    </generic-modal>

    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Services
import { modules } from '@/services/offers'
// Helpers
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { cloneDeep, startCase } from 'lodash-es'
// Mixins
import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
import RedirectHandler from '@/mixins/redirect-handler'
// Components
import BaseChip from '@/components/chips/base-chip'
import ExitModal from '@/components/modals/exit-modal'
import FabActions from '@/components/fab-actions'
import FieldBlockGroup from '@/views/modules/components/field-block-group'
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import ResetModal from '@/components/modals/reset-modal'
import TextInput from '@/components/forms/text-input'

const blankFormData = () => ({
  name: undefined,
  title: undefined,
  fields: [],
})

export default {
  name: 'ModuleEditor',
  components: {
    BaseChip,
    ExitModal,
    FabActions,
    FieldBlockGroup,
    GenericModal,
    PageLoader,
    ResetModal,
    TextInput,
  },
  mixins: [DataModification, PermissionsHandler, RedirectHandler],
  data: () => ({
    formData: blankFormData(),
    isLoading: false,
    original: blankFormData(),
    showDeleteModal: false,
    showResetModal: false,
    showSaveModal: false,
  }),
  computed: {
    ...mapGetters({
      pageTypes: 'pageTypes/getPageTypes',
      getItemsForOption: 'options/getItemsForOption',
      formattedOption: 'options/formattedOption',
    }),
    breadcrumbs() {
      return [
        { text: 'Module Management', to: { name: 'modules.management' }, exact: true },
        { text: this.original.title || this.original.name, disabled: true },
      ]
    },
    duplicateKeys() {
      const getKeys = (fields, path) => {
        return fields.reduce((acc, field) => {
          const pathKey = path ? [path, field.key].join('.') : field.key
          if (field.fields && field.fields.length) {
            return [...acc, pathKey, ...getKeys(field.fields, pathKey)]
          }
          return [...acc, pathKey]
        }, [])
      }

      const keys = getKeys(this.formData.fields)
      const duplicates = new Set()
      keys.forEach(key => {
        if (key && keys.filter(k => k === key).length > 1) {
          duplicates.add(key)
        }
      })

      return Array.from(duplicates)
    },
    hasChanges() {
      return this.hasDataChanged(this.formData, this.original)
    },
    pageTypesUsingModule() {
      return this.pageTypes.data
        .filter(({ schema: { sections } }) =>
          Object.values(sections)
            .flat()
            .find(({ id }) => this.formData.id === id)
        )
        .map(({ id, name }) => ({
          id,
          name: startCase(name),
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },
  },
  async created() {
    await Promise.all([this.getModule(), this.getPageTypes({ paginate: { perPage: null } })])

    this.setBeforeUnloadListener(this.onBeforeUnload)
    this.setPageLoading(false)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading']),
    ...mapActions({ getPageTypes: 'pageTypes/fetch' }),
    startCase,
    async getModule() {
      this.isLoading = true
      try {
        this.formData = await modules.getModule(this.$route.params.id)
        this.original = cloneDeep(this.formData)
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve module',
          data: { error },
          duration: -1,
        })
        this.$_redirectHandler_404({ name: 'modules.management' })
      }
      this.isLoading = false
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    async onDeleteConfirm() {
      this.isLoading = true

      try {
        await modules.deleteModule(this.formData)
        this.$notify({
          type: 'success',
          text: `Successfully removed module '${this.original.title || this.original.name}'`,
        })
        this.$router.push({ name: 'modules.management' })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete module',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    onResetConfirm() {
      this.formData = cloneDeep(this.original)
      this.showResetModal = false
    },
    async onSaveConfirm() {
      try {
        if (!this.$refs.form.validate()) throw 'Missing required fields'
        if (this.duplicateKeys.length) throw 'Duplicate keys detected'

        this.isLoading = true

        await modules.updateModule(this.formData.id, this.formData)
        this.original = cloneDeep(this.formData)
        this.$notify({
          type: 'success',
          text: 'Successfully saved module',
        })

        this.$refs['field-block-group'].lockKeys()
        this.showSaveModal = false
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save module',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
  },
}
</script>
